import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";
import { ParallaxImage } from "@/components/ParallaxImage";
import { Counter } from "@/components/Counter";
import { Marquee } from "@/components/Marquee";
import { IMG } from "@/data/images";

const PILLARS_2030 = [
  { title: "Food Security", text: "Reducing post-harvest loss can strengthen the Kingdom's food resilience without demanding more land or water." },
  { title: "Environmental Sustainability", text: "Every tonne of produce saved can avoid the emissions, water and waste footprint of producing and disposing of it." },
  { title: "Quality of Life", text: "Fresher, longer-lasting produce can support better nutrition and less household food waste." },
  { title: "Economic Diversification", text: "Fresh Guard aims to build non-oil, knowledge-based industry — food science, smart packaging and agri-data — in the Kingdom." },
];

const CIRCULAR = [
  { n: "01", title: "Source responsibly", text: "We aim to work with growers and importers who share our quality and traceability standards." },
  { n: "02", title: "Protect naturally", text: "Plant-derived formulations in development, designed to extend life without a synthetic preservative burden." },
  { n: "03", title: "Deliver efficiently", text: "Cold-chain intelligence is being designed to reduce spoilage, emergency freight and over-ordering." },
  { n: "04", title: "Recover & learn", text: "Our goal: unavoidable waste streams directed to composting and recovery — with data improving the next cycle." },
];

export default function Sustainability() {
  return (
    <div data-testid="sustainability-page">
      <PageHero
        chapter="06"
        tag="Sustainability"
        testId="sustainability-hero"
        lines={[<>Waste less.</>, <>Feed more.</>]}
        lead="Sustainability is not a department at Fresh Guard — it is the ambition behind the product. Every day of added freshness has the potential to preserve water, energy and labour."
      />

      <Marquee
        items={["Saudi Vision 2030", "Food security", "Water stewardship", "Lower emissions", "Circular by design", "Less waste, more value"]}
        testId="sustainability-marquee"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
        <Reveal><Tag>Aligned with Saudi Vision 2030</Tag></Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {PILLARS_2030.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div data-testid={`vision2030-card-${i + 1}`} className="border border-forest/10 bg-white p-8 sm:p-10 h-full transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10">
                <span className="font-mono text-xs text-amber">0{i + 1}</span>
                <h3 className="mt-4 font-display font-bold text-2xl text-forest">{p.title}</h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-moss">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-deep text-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
          <Reveal><Tag light>The hidden footprint of wasted food</Tag></Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-12">
            <Counter dark value={25} suffix="%" label="of agricultural water grows food that is never eaten" sub="Published estimates" testId="sustainability-counter-water" />
            <Counter dark value={10} suffix="%" label="of global greenhouse-gas emissions are linked to food loss and waste" sub="UNEP estimate" testId="sustainability-counter-ghg" />
            <Counter dark value={4} suffix="M t" label="of food estimated to be wasted annually in Saudi Arabia" sub="National studies" testId="sustainability-counter-ksa" />
          </div>
          <Reveal delay={0.2}>
            <p className="mt-14 font-serif italic text-2xl sm:text-3xl leading-snug text-cream/90 max-w-3xl">
              &ldquo;The most sustainable calorie is the one that never had to be grown twice.&rdquo;
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <Reveal><Tag>Circular by design</Tag></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest">
                A system, not a product.
              </h2>
            </Reveal>
            <div className="mt-10 space-y-0">
              {CIRCULAR.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.08}>
                  <div className="border-t border-forest/10 py-6 last:border-b" data-testid={`circular-step-${c.n}`}>
                    <div className="flex items-baseline gap-6">
                      <span className="font-mono text-xs text-amber shrink-0">{c.n}</span>
                      <div>
                        <h3 className="font-display font-bold text-lg text-forest">{c.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-moss">{c.text}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:sticky lg:top-28">
            <Reveal delay={0.15}>
              <ParallaxImage src={IMG.earthSeedling} alt="Hands holding a seedling — growth and stewardship" ratio="aspect-[4/3]" speed={8} testId="sustainability-image-earth" />
            </Reveal>
            <Reveal delay={0.25}>
              <ParallaxImage src={IMG.greenHills} alt="Lush green hills under open sky" ratio="aspect-[16/9]" speed={12} testId="sustainability-image-hills" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 text-center">
        <Reveal>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest max-w-2xl mx-auto">
            Let&rsquo;s scale this impact together.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/contact"
            data-testid="sustainability-cta-contact"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-emerald"
          >
            Start a conversation
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
